import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import { useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import Clients from './components/Clients';
import Projects from './components/Projects';
import Departments from './components/Departments';
import Invoices from './components/Invoices';
import Payments from './components/Payments';
import Items from './components/Items';
import Suppliers from './components/Suppliers';
import PurchaseOrders from './components/PurchaseOrders';
import Notes from './components/Notes';
import SalaryChart from './components/SalaryChart';
import Payroll from './components/Payroll';
import SalaryAdvance from './components/SalaryAdvance';
import Loans from './components/Loans';
import Rejoinings from './components/Rejoinings';
import TeamMembers from './components/TeamMembers';
import TeamMemberDetails from './components/TeamMemberDetails';
import TeamLeave from './components/TeamLeave';
import TeamTimeCards from './components/TeamTimeCards';
import TeamAnnouncements from './components/TeamAnnouncements';
import Expenses from './components/Expenses';
import Files from './components/Files';
import Contacts from './components/Contacts';
import Settings from './components/Settings';
import Logs from './components/Logs';
import Placeholder from './components/Placeholder';
import Tenders from './components/Tenders';
import TenderList from './components/TenderList';
import TenderDetails from './components/TenderDetails';
import ProposalList from './components/ProposalList';
import ProposalDetails from './components/ProposalDetails';
import Procurement from './components/Procurement';
import Taskboard from './components/Taskboard';
import ProjectKanban from './components/ProjectKanban';
import SupabaseTest from './components/SupabaseTest';
import DepartmentsTest from './components/DepartmentsTest';
import OfficialDocuments from './components/OfficialDocuments';
import TenderModal from './TenderAI/Aitest';
import ResetPassword from './components/ResetPassword';
import AuthButtonsTest from './components/AuthButtonsTest';

function App() {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Normalize trailing slash: Supabase links might include `/reset-password/`.
  const normalizedPath =
    typeof location?.pathname === 'string' ? location.pathname.replace(/\/$/, '') : location?.pathname;
  const isResetPassword = normalizedPath === '/reset-password';
  const isAuthTest = location.pathname === '/auth-test';

  if (loading) {
    return (
      <div className="App-loading">
        <p>Loading…</p>
      </div>
    );
  }

  if (isResetPassword) {
    return <ResetPassword />;
  }

  if (!user && !isAuthTest) {
    return <Login />;
  }

  return (
    <Routes>
      <Route path="/auth-test" element={<Layout><AuthButtonsTest /></Layout>} />
      <Route path="/" element={<ProtectedRoute><Layout><Dashboard /></Layout></ProtectedRoute>} />
      <Route path="/clients" element={<ProtectedRoute><Layout><Clients /></Layout></ProtectedRoute>} />
      <Route path="/projects" element={<ProtectedRoute><Layout><Projects /></Layout></ProtectedRoute>} />
      <Route path="/departments" element={<ProtectedRoute><Layout><Departments /></Layout></ProtectedRoute>} />
      <Route path="/departments/contacts" element={<ProtectedRoute><Layout><Contacts /></Layout></ProtectedRoute>} />
      <Route path="/taskboard" element={<ProtectedRoute><Layout><Taskboard /></Layout></ProtectedRoute>} />
      <Route path="/taskboard/:projectId" element={<ProtectedRoute><Layout><ProjectKanban /></Layout></ProtectedRoute>} />
      <Route path="/sales" element={<ProtectedRoute><Layout><Placeholder title="Sales" /></Layout></ProtectedRoute>} />
      <Route path="/sales/invoices" element={<ProtectedRoute><Layout><Invoices /></Layout></ProtectedRoute>} />
      <Route path="/sales/payments" element={<ProtectedRoute><Layout><Payments /></Layout></ProtectedRoute>} />
      <Route path="/sales/items" element={<ProtectedRoute><Layout><Items /></Layout></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><Layout><Suppliers /></Layout></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute><Layout><PurchaseOrders /></Layout></ProtectedRoute>} />
      <Route path="/notes" element={<ProtectedRoute><Layout><Notes /></Layout></ProtectedRoute>} />
      <Route path="/hr" element={<ProtectedRoute><Layout><Placeholder title="Human Resources" /></Layout></ProtectedRoute>} />
      <Route path="/hr/salary-chart" element={<ProtectedRoute><Layout><SalaryChart /></Layout></ProtectedRoute>} />
      <Route path="/hr/payroll" element={<ProtectedRoute><Layout><Payroll /></Layout></ProtectedRoute>} />
      <Route path="/hr/salary-advance" element={<ProtectedRoute><Layout><SalaryAdvance /></Layout></ProtectedRoute>} />
      <Route path="/hr/loans" element={<ProtectedRoute><Layout><Loans /></Layout></ProtectedRoute>} />
      <Route path="/hr/leave-request" element={<ProtectedRoute><Layout><TeamLeave /></Layout></ProtectedRoute>} />
      <Route path="/hr/rejoinings" element={<ProtectedRoute><Layout><Rejoinings /></Layout></ProtectedRoute>} />
      <Route path="/team" element={<ProtectedRoute><Layout><Placeholder title="Team" /></Layout></ProtectedRoute>} />
      <Route path="/team/members" element={<ProtectedRoute><Layout><TeamMembers /></Layout></ProtectedRoute>} />
      <Route path="/team/members/:id" element={<ProtectedRoute><Layout><TeamMemberDetails /></Layout></ProtectedRoute>} />
      <Route path="/team/leave" element={<ProtectedRoute><Layout><TeamLeave /></Layout></ProtectedRoute>} />
      <Route path="/team/time-cards" element={<ProtectedRoute><Layout><TeamTimeCards /></Layout></ProtectedRoute>} />
      <Route path="/team/announcements" element={<ProtectedRoute><Layout><TeamAnnouncements /></Layout></ProtectedRoute>} />
      <Route path="/expenses" element={<ProtectedRoute><Layout><Expenses /></Layout></ProtectedRoute>} />
      <Route path="/files" element={<ProtectedRoute><Layout><Files /></Layout></ProtectedRoute>} />
      <Route path="/procurement" element={<ProtectedRoute><Layout><Procurement /></Layout></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Layout><Settings /></Layout></ProtectedRoute>} />
      <Route
        path="/settings/user-access"
        element={
          <ProtectedRoute>
            <Navigate to="/team/members" replace />
          </ProtectedRoute>
        }
      />
      <Route path="/logs" element={<ProtectedRoute><Layout><Logs /></Layout></ProtectedRoute>} />
      {/* Debug route: no ProtectedRoute so it always renders once logged in */}
      <Route path="/supabase-test" element={<Layout><SupabaseTest /></Layout>} />
      <Route path="/departments-test" element={<Layout><DepartmentsTest /></Layout>} />
      {/* AI test route for Tender AI modal */}
      <Route path="/ai-test" element={<Layout><TenderModal /></Layout>} />
      <Route path="/tenders" element={<ProtectedRoute><Layout><Tenders /></Layout></ProtectedRoute>}>
        <Route index element={<TenderList />} />
        <Route path="proposals" element={<ProposalList />} />
        <Route path="proposals/:id" element={<ProposalDetails />} />
        <Route path=":id" element={<TenderDetails />} />
      </Route>
      <Route path="/tenders/official-documents" element={<ProtectedRoute><Layout><OfficialDocuments /></Layout></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
